/**
 * Platform SaaS Service
 * Migrado de Supabase a API REST
 */

const API_BASE = import.meta.env.VITE_PLATFORM_API_BASE_URL || '/api/platform';

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('platform_jwt');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('platform_jwt');
    }
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  
  return response.json();
}

// ============================================
// TYPES
// ============================================

// Claves de features disponibles en planes (ordenadas por categoría).
export type PlanFeatureKey =
  | 'tpv' | 'tienda_online' | 'whatsapp_link' | 'stock_realtime' | 'impresion_termica' | 'catalogo_fotos' | 'custom_domain'
  | 'suppliers' | 'purchases_basic' | 'purchases_history' | 'purchases_predictions' | 'purchases_api' | 'stock_branch' | 'stock_alerts' | 'bulk_actions' | 'stock_movements'
  | 'staff_management' | 'roles_permissions' | 'activity_logs' | 'activity_logs_export' | 'expenses' | 'cash_register'
  | 'crm' | 'customer_history' | 'loyalty_points' | 'coupons'
  | 'dashboard_basic' | 'dashboard_financial' | 'dashboard_advanced' | 'dashboard_custom' | 'web_analytics' | 'woo_import'
  | 'api_access' | 'webhooks' | 'priority_support' | 'assisted_migration';

// Claves de límites disponibles en planes.
export type PlanLimitKey =
  | 'max_products' | 'max_branches' | 'max_users' | 'max_users_per_branch' | 'max_categories'
  | 'max_orders_per_month' | 'storage_mb' | 'analytics_retention_days' | 'activity_logs_days';

export type PlanFeatures = Partial<Record<PlanFeatureKey, boolean>>;
export type PlanLimits = Partial<Record<PlanLimitKey, number | null>>;

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  price_monthly: number;
  price_yearly?: number | null;
  currency: string;
  max_branches: number;
  max_users: number;
  max_products: number;
  features: PlanFeatures;
  limits: PlanLimits;
  trial_days: number;
  pricing_model: string;
  is_active: boolean;
  is_highlighted: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Tenant {
  id: string;
  account_id?: string | null;
  slug: string;
  business_name: string;
  legal_name?: string;
  email?: string;
  phone?: string;
  logo_url?: string;
  custom_domain?: string | null;
  provision_status?: string;
  tenant_version?: string;
  desired_version?: string;
  current_version?: string | null;
  /** Versión del Sitio web. null = igual a la versión de Sistema (current_version). */
  current_site_version?: string | null;
  desired_site_version?: string | null;
  last_release_status?: string;
  last_release_error?: string | null;
  plan_id?: string;
  plan_name?: string;
  plan_slug?: string;
  plan_price?: number;
  subscription_status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'suspended';
  is_active: boolean;
  trial_ends_at?: string;
  subscription_ends_at?: string;
  current_products_count: number;
  current_orders_month: number;
  current_storage_mb: number;
  enabled_features?: string[] | null;
  address_line1?: string;
  address_city?: string;
  address_state?: string;
  country_id?: string;
  country_code?: string;
  country_name?: string;
  tax_regime_id?: string | null;
  tax_regime_code?: string | null;
  tax_regime_name?: string | null;
  currency_code?: string | null;
  country_currency_code?: string | null;
  openai_api_key?: string | null;
  openai_api_key_configured?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Account {
  id: string;
  name: string;
  legal_name?: string;
  email: string;
  phone?: string;
  tax_id?: string;
  status: 'active' | 'suspended' | 'cancelled';
  billing_email?: string;
  address?: string;
  city?: string;
  state?: string;
  country_id?: string;
  owner_user_id?: string | null;
  tenants?: AccountTenant[];
  subscriptions_summary?: {
    active: number;
    past_due: number;
    cancelled: number;
    total: number;
  };
  created_at: string;
  updated_at: string;
}

export interface AccountTenant {
  id: string;
  slug: string;
  business_name: string;
  subscription_status?: string;
  plan_id?: string | null;
  trial_ends_at?: string | null;
  subscription_ends_at?: string | null;
  custom_domain?: string | null;
  current_version?: string | null;
  desired_version?: string | null;
  is_active?: boolean;
  created_at: string;
}

export interface Subscription {
  id: string;
  account_id: string;
  plan_id: string;
  billing_cycle: 'monthly' | 'yearly';
  status: 'active' | 'past_due' | 'cancelled' | 'trialing';
  started_at: string;
  current_period_start: string;
  current_period_end?: string;
  trial_ends_at?: string;
  cancelled_at?: string;
  plan_name?: string;
  plan_price?: number;
  account_name?: string;
  account_email?: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardStats {
  totalTenants: number;
  activeTenants: number;
  trialingTenants: number;
  newTenantsThisMonth: number;
  totalRevenue: number;
  monthlyRevenue: number;
  churnRate: number;
  planDistribution: Array<{ plan: string; count: number }>;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============================================
// DASHBOARD
// ============================================

export async function getDashboardStats(): Promise<DashboardStats> {
  return fetchAPI<DashboardStats>('/stats');
}

// ============================================
// AUTH
// ============================================

export async function platformLogin(email: string, password: string): Promise<{ token: string; user: { email: string; role: string } }> {
  return fetchAPI('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export interface PlatformMe {
  id?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  whatsapp_phone?: string | null;
}

export async function getMe(): Promise<PlatformMe> {
  return fetchAPI<PlatformMe>('/auth/me');
}

// ============================================
// PLATFORM USERS  (/users)
// ============================================

export type PlatformRole = 'owner' | 'admin' | 'support' | 'finance' | 'viewer';

export interface PlatformUser {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  whatsapp_phone?: string | null;
  role: PlatformRole;
  is_active: boolean;
  last_login_at?: string | null;
  invited_at?: string | null;
  created_at: string;
  [key: string]: unknown;
}

export async function getPlatformUsers(options?: {
  role?: PlatformRole;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<PlatformUser>> {
  const params = new URLSearchParams();
  if (options?.role) params.set('role', options.role);
  if (typeof options?.isActive === 'boolean') params.set('is_active', String(options.isActive));
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<PlatformUser>>(`/users${query ? `?${query}` : ''}`);
}

export async function invitePlatformUser(body: {
  email: string;
  role: PlatformRole;
  first_name?: string | null;
  last_name?: string | null;
  whatsapp_phone?: string | null;
}): Promise<PlatformUser> {
  return fetchAPI<PlatformUser>('/users', { method: 'POST', body: JSON.stringify(body) });
}

export async function updatePlatformUser(id: string, body: Partial<{
  first_name: string | null;
  last_name: string | null;
  whatsapp_phone: string | null;
  role: PlatformRole;
  is_active: boolean;
}>): Promise<PlatformUser> {
  return fetchAPI<PlatformUser>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deletePlatformUser(id: string): Promise<{ success: boolean }> {
  return fetchAPI<{ success: boolean }>(`/users/${id}`, { method: 'DELETE' });
}

export async function requestTenantSuperadminAccess(tenantId: string, reason: string): Promise<{
  request_id: string;
  status: string;
  expires_at: string;
  whatsapp_phone: string;
}> {
  return fetchAPI(`/tenants/${tenantId}/superadmin-access`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function approveTenantSuperadminAccess(token: string): Promise<{
  ok: boolean;
  request_id: string;
  url: string;
  expires_at: string;
}> {
  return fetchAPI('/superadmin-access/approve', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function resendUserInvite(id: string): Promise<{ success: boolean }> {
  return fetchAPI<{ success: boolean }>(`/users/${id}/resend-invite`, { method: 'POST' });
}

// ============================================
// PLANS
// ============================================

export async function getPlans(): Promise<Plan[]> {
  const res = await fetchAPI<PaginatedResponse<Plan> | Plan[]>('/plans');
  return Array.isArray(res) ? res : (res.data || []);
}

export async function getPlanById(id: string): Promise<Plan> {
  return fetchAPI<Plan>(`/plans/${id}`);
}

export async function createPlan(plan: Partial<Plan>): Promise<Plan> {
  return fetchAPI<Plan>('/plans', {
    method: 'POST',
    body: JSON.stringify(plan),
  });
}

export async function updatePlan(id: string, plan: Partial<Plan>): Promise<Plan> {
  return fetchAPI<Plan>(`/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(plan),
  });
}

export async function deletePlan(id: string): Promise<void> {
  await fetchAPI<{ success: boolean }>(`/plans/${id}`, {
    method: 'DELETE',
  });
}

// ============================================
// TENANTS
// ============================================

export async function getTenants(options?: {
  status?: string;
  planId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<Tenant>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.planId) params.set('plan_id', options.planId);
  if (options?.search) params.set('search', options.search);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  
  const query = params.toString();
  return fetchAPI<PaginatedResponse<Tenant>>(`/tenants${query ? `?${query}` : ''}`);
}

export async function getTenantById(id: string): Promise<Tenant> {
  return fetchAPI<Tenant>(`/tenants/${id}`);
}

export async function updateTenant(id: string, tenant: Partial<Tenant>): Promise<Tenant> {
  return fetchAPI<Tenant>(`/tenants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(tenant),
  });
}

export async function changeTenantPlan(tenantId: string, planId: string): Promise<Tenant> {
  return updateTenant(tenantId, { plan_id: planId } as Partial<Tenant>);
}

export async function suspendTenant(tenantId: string): Promise<Tenant> {
  return fetchAPI<Tenant>(`/tenants/${tenantId}/suspend`, { method: 'POST' });
}

export async function activateTenant(tenantId: string): Promise<Tenant> {
  return fetchAPI<Tenant>(`/tenants/${tenantId}/activate`, { method: 'POST' });
}

export async function deprovisionTenant(tenantId: string, confirmSlug: string, options?: { purge?: boolean }): Promise<{ success: boolean }> {
  return fetchAPI<{ success: boolean }>(`/tenants/${tenantId}/deprovision`, {
    method: 'POST',
    body: JSON.stringify({ confirm_slug: confirmSlug, purge: options?.purge ?? false }),
  });
}

export interface ReleaseCatalogItem {
  id: string;
  release_tag: string;
  channel: string;
  source_ref?: string | null;
  commit_sha?: string | null;
  short_sha?: string | null;
  actor?: string | null;
  event?: string | null;
  run_id?: string | null;
  run_url?: string | null;
  registry_host?: string | null;
  tenant_api_image?: string | null;
  tenant_admin_image?: string | null;
  tenant_storefront_image?: string | null;
  tenant_web_image?: string | null;
  platform_web_image?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  promoted_at?: string | null;
}

export interface TenantStageInstance {
  id: string;
  tenant_id: string;
  public_slug: string;
  runtime_slug: string;
  environment: string;
  release_tag: string;
  status: 'requested' | 'deploying' | 'active' | 'error' | string;
  url?: string | null;
  portainer_stack_id?: number | null;
  last_error?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  deployed_at?: string | null;
}

export async function getReleasesCatalog(): Promise<ReleaseCatalogItem[]> {
  const response = await fetchAPI<{ data: ReleaseCatalogItem[] }>('/releases');
  return response.data || [];
}

export async function getTenantVersions(): Promise<string[]> {
  const response = await fetchAPI<{ data: string[] }>('/releases/versions');
  return response.data || [];
}

export interface TenantRelease {
  id: string;
  action: string;
  from_version?: string | null;
  to_version?: string | null;
  /** null en releases previos a la separación Sistema/Sitio (= from_version / to_version). */
  from_site_version?: string | null;
  to_site_version?: string | null;
  status: string;
  snapshot_id?: string | null;
  snapshot_path?: string | null;
  snapshot_status?: string | null;
  created_at: string;
  completed_at?: string | null;
  error?: string | null;
}

export async function getTenantReleases(tenantId: string): Promise<TenantRelease[]> {
  const response = await fetchAPI<{ data: TenantRelease[] }>(`/tenants/${tenantId}/releases`);
  return response.data || [];
}

export async function getTenantStage(tenantId: string): Promise<TenantStageInstance | null> {
  const response = await fetchAPI<{ stage: TenantStageInstance | null }>(`/tenants/${tenantId}/stage`);
  return response.stage || null;
}

export async function upsertTenantStage(tenantId: string, releaseTag: string): Promise<TenantStageInstance> {
  const response = await fetchAPI<{ stage: TenantStageInstance }>(`/tenants/${tenantId}/stage`, {
    method: 'POST',
    body: JSON.stringify({ release_tag: releaseTag }),
  });
  return response.stage;
}

export async function deleteTenantStage(tenantId: string, confirmSlug: string): Promise<{ ok: boolean }> {
  return fetchAPI<{ ok: boolean }>(`/tenants/${tenantId}/stage`, {
    method: 'DELETE',
    body: JSON.stringify({ confirm_slug: confirmSlug }),
  });
}

export type ProvisionJobStatus = 'requested' | 'running' | 'success' | 'error';

export interface ProvisionJob {
  id: string;
  tenant_id?: string | null;
  tenant_slug?: string | null;
  tenant_business_name?: string | null;
  action: string;
  status: ProvisionJobStatus;
  duration_ms?: number | null;
  error?: string | null;
  created_at: string;
  completed_at?: string | null;
  payload?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export async function getProvisionJobs(options?: {
  tenantId?: string;
  status?: ProvisionJobStatus;
  action?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<ProvisionJob>> {
  const params = new URLSearchParams();
  if (options?.tenantId) params.set('tenant_id', options.tenantId);
  if (options?.status) params.set('status', options.status);
  if (options?.action) params.set('action', options.action);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<ProvisionJob>>(`/provision-jobs${query ? `?${query}` : ''}`);
}

export async function getProvisionJob(id: string): Promise<ProvisionJob> {
  return fetchAPI<ProvisionJob>(`/provision-jobs/${id}`);
}

/** Sistema = API + admin (van juntos). Único componente actualizable: el sitio web vive en su propio repositorio. */
export type TenantUpgradeComponent = 'system'

export interface TenantUpgradeInput {
  /** Versión de Sistema a la que actualizar. */
  version?: string;
  components: TenantUpgradeComponent[];
  /** Confirma un upgrade cuya versión el catálogo no puede ordenar. Queda en el historial. */
  force?: boolean;
}

export async function upgradeTenant(tenantId: string, input: TenantUpgradeInput): Promise<{
  tenant_id: string;
  components?: TenantUpgradeComponent[];
  force?: boolean;
  from_version: string;
  to_version: string;
  current_version: string;
  from_site_version?: string;
  to_site_version?: string;
  current_site_version?: string;
  snapshot: { id: string; snapshot_path: string; source_version: string; target_version?: string | null };
}> {
  return fetchAPI(`/tenants/${tenantId}/upgrade`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function rollbackTenant(tenantId: string): Promise<{
  tenant_id: string;
  restored_snapshot_id: string;
  current_version: string;
  current_site_version?: string;
}> {
  return fetchAPI(`/tenants/${tenantId}/rollback`, {
    method: 'POST',
  });
}

// ============================================
// INFRA
// ============================================

export interface RegistryStatus {
  configured: boolean;
  registry: null | {
    id: number | string | null;
    name?: string;
    url: string;
    type?: number;
    authentication?: boolean;
    username?: string | null;
  };
}

export async function getRegistryStatus(): Promise<RegistryStatus> {
  return fetchAPI<RegistryStatus>('/infra/registry/status');
}

export async function setupRegistry(): Promise<{
  ok: boolean;
  action: 'created' | 'updated';
  registry: { id: number | string | null; url: string; username: string };
  probe: { ok: boolean; status: number };
}> {
  return fetchAPI('/infra/registry/setup', { method: 'POST' });
}

// ============================================
// PLATFORM SETTINGS
// ============================================

export interface PlatformSettings {
  google_maps_api_key_configured: boolean;
  gemini_api_key_configured: boolean;
  meta_ads_enabled: boolean;
  meta_pixel_id: string | null;
  meta_capi_token_configured: boolean;
  meta_test_event_code: string | null;
  updated_at: string | null;
}

export async function getPlatformSettings(): Promise<PlatformSettings> {
  return fetchAPI<PlatformSettings>('/settings');
}

export async function updatePlatformSettings(settings: {
  google_maps_api_key?: string | null;
  gemini_api_key?: string | null;
  meta_ads_enabled?: boolean;
  meta_pixel_id?: string | null;
  meta_capi_token?: string | null;
  meta_test_event_code?: string | null;
}): Promise<PlatformSettings> {
  return fetchAPI<PlatformSettings>('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ============================================
// ACCOUNTS
// ============================================

export async function getAccounts(options?: {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<Account>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.search) params.set('search', options.search);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  
  const query = params.toString();
  return fetchAPI<PaginatedResponse<Account>>(`/accounts${query ? `?${query}` : ''}`);
}

export async function getAccount(id: string): Promise<Account> {
  return fetchAPI<Account>(`/accounts/${id}`);
}

export async function createAccount(account: Partial<Account> & { name: string; email: string }): Promise<Account> {
  return fetchAPI<Account>('/accounts', {
    method: 'POST',
    body: JSON.stringify(account),
  });
}

export async function updateAccount(id: string, account: Partial<Account>): Promise<Account> {
  return fetchAPI<Account>(`/accounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(account),
  });
}

export async function deleteAccount(id: string): Promise<void> {
  await fetchAPI<{ success: boolean }>(`/accounts/${id}`, { method: 'DELETE' });
}

export async function createTenant(tenant: {
  slug: string;
  business_name: string;
  country_code?: string;
  currency_id?: string;
  tax_regime_id?: string | null;
  account_id?: string | null;
  plan_id?: string | null;
  theme_id?: string;
  custom_domain?: string | null;
  tenant_version?: string;
  enabled_features?: string[];
}): Promise<Tenant> {
  return fetchAPI<Tenant>('/tenants', {
    method: 'POST',
    body: JSON.stringify(tenant),
  });
}

export async function provisionTenant(tenantId: string, opts?: { admin_email?: string; admin_password?: string; version?: string }): Promise<{
  tenant: Tenant;
  urls: { storefront: string; admin: string; api: string };
  admin: { email: string; password: string };
}> {
  return fetchAPI(`/tenants/${tenantId}/provision`, {
    method: 'POST',
    body: JSON.stringify(opts || {}),
  });
}

export async function applyTenantCustomDomain(tenantId: string, customDomain: string): Promise<{
  tenant_id: string;
  slug: string;
  custom_domain: string;
}> {
  return fetchAPI(`/tenants/${tenantId}/custom-domain`, {
    method: 'POST',
    body: JSON.stringify({ custom_domain: customDomain }),
  });
}

// ============================================
// SUBSCRIPTIONS
// ============================================

export async function getSubscriptions(options?: {
  status?: string;
  accountId?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<Subscription>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.accountId) params.set('account_id', options.accountId);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  
  const query = params.toString();
  return fetchAPI<PaginatedResponse<Subscription>>(`/subscriptions${query ? `?${query}` : ''}`);
}

// ============================================
// CATALOGS
// ============================================

export interface FeatureCatalog {
  key: PlanFeatureKey;
  name: string;
  description?: string | null;
  category: string;
  icon?: string | null;
  sort_order: number;
  is_active?: boolean;
  created_at?: string;
}

export interface FeatureCategoryCatalog {
  key: string;
  label: string;
  description?: string | null;
  icon?: string | null;
  sort_order: number;
  is_active?: boolean;
}

export interface LimitCatalog {
  key: PlanLimitKey;
  name: string;
  description?: string | null;
  unit?: string | null;
  unlimited_value: string;
  sort_order: number;
}

export async function getFeatureCatalog(): Promise<FeatureCatalog[]> {
  return fetchAPI<FeatureCatalog[]>('/features');
}

export async function getFeatureCategories(options?: { includeInactive?: boolean }): Promise<FeatureCategoryCatalog[]> {
  const query = options?.includeInactive ? '?include_inactive=true' : '';
  return fetchAPI<FeatureCategoryCatalog[]>(`/feature-categories${query}`);
}

export async function updateFeatureCatalog(key: string, feature: Partial<Pick<FeatureCatalog, 'name' | 'description' | 'category' | 'icon' | 'sort_order' | 'is_active'>>): Promise<FeatureCatalog> {
  return fetchAPI<FeatureCatalog>(`/features/${key}`, {
    method: 'PATCH',
    body: JSON.stringify(feature),
  });
}

export async function getLimitCatalog(): Promise<LimitCatalog[]> {
  return fetchAPI<LimitCatalog[]>('/limits');
}

// ============================================
// COUNTRIES & TAX REGIMES
// ============================================

export interface Country {
  id: string;
  code: string;
  name: string;
  currency_code: string;
  locale: string;
  timezone: string;
  tax_id_label: string;
  tax_id_pattern?: string | null;
  phone_prefix?: string | null;
}

export interface TaxRegime {
  id: string;
  code: string;
  name: string;
  country_id: string;
  description?: string;
  is_default?: boolean;
  properties?: Record<string, unknown>;
}

export async function getCountries(): Promise<Country[]> {
  return fetchAPI<Country[]>('/countries');
}

export async function getTaxRegimes(countryId?: string): Promise<TaxRegime[]> {
  const query = countryId ? `?country_id=${countryId}` : '';
  return fetchAPI<TaxRegime[]>(`/tax-regimes${query}`);
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimal_places: number;
}

export async function getCurrencies(): Promise<Currency[]> {
  return fetchAPI<Currency[]>('/currencies');
}

// ============================================
// AUDIT LOGS
// ============================================

export interface AuditLog {
  id: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  tenant_id?: string;
  account_id?: string;
  user_id?: string;
  old_data?: Record<string, unknown>;
  new_data?: Record<string, unknown>;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
}

export async function getAuditLogs(options?: {
  tenantId?: string;
  accountId?: string;
  action?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<AuditLog>> {
  const params = new URLSearchParams();
  if (options?.tenantId) params.set('tenant_id', options.tenantId);
  if (options?.accountId) params.set('account_id', options.accountId);
  if (options?.action) params.set('action', options.action);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  
  const query = params.toString();
  return fetchAPI<PaginatedResponse<AuditLog>>(`/audit-logs${query ? `?${query}` : ''}`);
}

// ============================================
// ONBOARDING LEADS  (/onboarding-leads)
// ============================================

export type LeadWorkedStatus = 'pending' | 'contacted' | 'converted' | 'lost';

export interface OnboardingLead {
  id: string;
  business_name?: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  plan_slug?: string | null;
  worked_status: LeadWorkedStatus;
  status?: string;
  notes?: string | null;
  created_at: string;
  updated_at?: string;
  [key: string]: unknown;
}

export async function getOnboardingLeads(options?: {
  workedStatus?: LeadWorkedStatus;
  status?: string;
  planSlug?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<OnboardingLead>> {
  const params = new URLSearchParams();
  if (options?.workedStatus) params.set('worked_status', options.workedStatus);
  if (options?.status) params.set('status', options.status);
  if (options?.planSlug) params.set('plan_slug', options.planSlug);
  if (options?.search) params.set('search', options.search);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<OnboardingLead>>(`/onboarding-leads${query ? `?${query}` : ''}`);
}

export async function getOnboardingLead(id: string): Promise<OnboardingLead> {
  return fetchAPI<OnboardingLead>(`/onboarding-leads/${id}`);
}

export async function updateOnboardingLead(
  id: string,
  body: { worked_status?: LeadWorkedStatus; notes?: string | null },
): Promise<OnboardingLead> {
  return fetchAPI<OnboardingLead>(`/onboarding-leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function convertOnboardingLead(id: string, body?: Record<string, unknown>): Promise<OnboardingLead> {
  return fetchAPI<OnboardingLead>(`/onboarding-leads/${id}/convert`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

// ============================================
// WEBSITE LEADS — contacto comercial  (/website-leads)
// ============================================

export type CommercialStatus = 'new' | 'contacted' | 'qualified' | 'discarded';

export interface WebsiteLead {
  id: string;
  name?: string;
  business?: string;
  email?: string;
  phone?: string;
  message?: string | null;
  source?: string | null;
  commercial_status: CommercialStatus;
  created_at: string;
  [key: string]: unknown;
}

export async function getWebsiteLeads(options?: {
  commercialStatus?: CommercialStatus;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<WebsiteLead>> {
  const params = new URLSearchParams();
  if (options?.commercialStatus) params.set('commercial_status', options.commercialStatus);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<WebsiteLead>>(`/website-leads${query ? `?${query}` : ''}`);
}

export async function updateWebsiteLead(id: string, commercialStatus: CommercialStatus): Promise<WebsiteLead> {
  return fetchAPI<WebsiteLead>(`/website-leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ commercial_status: commercialStatus }),
  });
}

// ============================================
// PLATFORM NOTIFICATIONS  (/notifications)
// ============================================

export type NotificationChannel = 'email';
export type NotificationAudience = 'single_tenant' | 'all_tenants' | 'plan_ids' | 'status';
export type NotificationStatus = 'draft' | 'queued' | 'sent' | 'failed';
export type NotificationDeliveryStatus = 'queued' | 'sent' | 'failed' | 'skipped';

export interface PlatformNotification {
  id: string;
  subject: string;
  body?: string;
  channel: NotificationChannel;
  audience: NotificationAudience;
  status: NotificationStatus;
  tenant_id?: string | null;
  plan_ids?: string[];
  subscription_status?: string | null;
  sent_at?: string | null;
  stats?: { recipients?: number; sent?: number; failed?: number; skipped?: number };
  created_at: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface NotificationDelivery {
  id: string;
  notification_id: string;
  tenant_id?: string | null;
  email?: string | null;
  status: NotificationDeliveryStatus;
  error?: string | null;
  created_at: string;
  [key: string]: unknown;
}

export interface NotificationPreview {
  recipients: number;
  with_email: number;
  without_email: number;
  sample: Array<Record<string, unknown>>;
}

export async function getNotifications(options?: {
  status?: NotificationStatus;
  channel?: NotificationChannel;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<PlatformNotification>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.channel) params.set('channel', options.channel);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<PlatformNotification>>(`/notifications${query ? `?${query}` : ''}`);
}

export async function getNotification(id: string): Promise<PlatformNotification> {
  return fetchAPI<PlatformNotification>(`/notifications/${id}`);
}

export async function createNotification(body: {
  subject: string;
  body: string;
  channel?: NotificationChannel;
  audience: NotificationAudience;
  tenant_id?: string;
  plan_ids?: string[];
  subscription_status?: string;
}): Promise<PlatformNotification> {
  return fetchAPI<PlatformNotification>('/notifications', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function previewNotificationAudience(body: {
  audience: NotificationAudience;
  tenant_id?: string;
  plan_ids?: string[];
  subscription_status?: string;
}): Promise<NotificationPreview> {
  return fetchAPI<NotificationPreview>('/notifications/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function sendNotification(id: string): Promise<PlatformNotification> {
  return fetchAPI<PlatformNotification>(`/notifications/${id}/send`, { method: 'POST' });
}

export async function getNotificationDeliveries(id: string, options?: {
  status?: NotificationDeliveryStatus;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<NotificationDelivery>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<NotificationDelivery>>(`/notifications/${id}/deliveries${query ? `?${query}` : ''}`);
}

// ============================================
// COUPONS  (/coupons)
// ============================================

export type CouponType = 'percent' | 'fixed';

export interface Coupon {
  id: string;
  code: string;
  type: CouponType;
  value: number;
  currency?: string | null;
  max_redemptions?: number | null;
  redemptions?: number;
  expires_at?: string | null;
  applies_to_plan_ids?: string[];
  is_active: boolean;
  description?: string | null;
  created_at: string;
  updated_at?: string;
}

export async function getCoupons(options?: {
  isActive?: boolean;
  type?: CouponType;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<Coupon>> {
  const params = new URLSearchParams();
  if (typeof options?.isActive === 'boolean') params.set('is_active', String(options.isActive));
  if (options?.type) params.set('type', options.type);
  if (options?.search) params.set('search', options.search);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<Coupon>>(`/coupons${query ? `?${query}` : ''}`);
}

export async function getCoupon(id: string): Promise<Coupon> {
  return fetchAPI<Coupon>(`/coupons/${id}`);
}

export async function createCoupon(body: {
  code: string;
  type: CouponType;
  value: number;
  currency?: string | null;
  max_redemptions?: number | null;
  expires_at?: string | null;
  applies_to_plan_ids?: string[];
  is_active?: boolean;
  description?: string | null;
}): Promise<Coupon> {
  return fetchAPI<Coupon>('/coupons', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateCoupon(id: string, body: Partial<{
  type: CouponType;
  value: number;
  currency: string | null;
  max_redemptions: number | null;
  expires_at: string | null;
  applies_to_plan_ids: string[];
  is_active: boolean;
  description: string | null;
}>): Promise<Coupon> {
  return fetchAPI<Coupon>(`/coupons/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deleteCoupon(id: string): Promise<{ success: boolean; hard_deleted?: boolean }> {
  return fetchAPI<{ success: boolean; hard_deleted?: boolean }>(`/coupons/${id}`, { method: 'DELETE' });
}

// ============================================
// INVOICES  (/invoices)
// ============================================

export type InvoiceStatus = 'draft' | 'pending' | 'open' | 'paid' | 'void' | 'uncollectible';

export interface Invoice {
  id: string;
  number?: string | null;
  account_id?: string | null;
  tenant_id?: string | null;
  subscription_id?: string | null;
  status: InvoiceStatus;
  currency?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  issued_at?: string | null;
  paid_at?: string | null;
  payment_method?: string | null;
  payment_reference?: string | null;
  pdf_url?: string | null;
  account_name?: string | null;
  account_email?: string | null;
  tenant_slug?: string | null;
  tenant_business_name?: string | null;
  subscription_plan_id?: string | null;
  created_at: string;
  [key: string]: unknown;
}

export async function getInvoices(options?: {
  status?: InvoiceStatus;
  accountId?: string;
  tenantId?: string;
  subscriptionId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<Invoice>> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.accountId) params.set('account_id', options.accountId);
  if (options?.tenantId) params.set('tenant_id', options.tenantId);
  if (options?.subscriptionId) params.set('subscription_id', options.subscriptionId);
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<Invoice>>(`/invoices${query ? `?${query}` : ''}`);
}

export async function getInvoice(id: string): Promise<Invoice> {
  return fetchAPI<Invoice>(`/invoices/${id}`);
}

export async function getInvoicePdf(id: string): Promise<{ id: string; pdf_url: string | null; message: string }> {
  return fetchAPI<{ id: string; pdf_url: string | null; message: string }>(`/invoices/${id}/pdf`);
}

export async function markInvoicePaid(id: string, body?: { payment_method?: string; paid_at?: string }): Promise<Invoice> {
  return fetchAPI<Invoice>(`/invoices/${id}/mark-paid`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

export async function voidInvoice(id: string, reason?: string | null): Promise<Invoice> {
  return fetchAPI<Invoice>(`/invoices/${id}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

// ============================================
// WEB CONTACTS  (/web-contacts)
// ============================================

export interface WebContact {
  id: string;
  email?: string | null;
  name?: string | null;
  is_identified?: boolean;
  converted?: boolean;
  score?: number;
  utm_source?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  created_at: string;
  [key: string]: unknown;
}

export async function getContacts(options?: {
  isIdentified?: boolean;
  converted?: boolean;
  minScore?: number;
  utmSource?: string;
  search?: string;
  sort?: 'score' | 'last_seen';
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<WebContact>> {
  const params = new URLSearchParams();
  if (typeof options?.isIdentified === 'boolean') params.set('is_identified', String(options.isIdentified));
  if (typeof options?.converted === 'boolean') params.set('converted', String(options.converted));
  if (typeof options?.minScore === 'number') params.set('min_score', String(options.minScore));
  if (options?.utmSource) params.set('utm_source', options.utmSource);
  if (options?.search) params.set('search', options.search);
  if (options?.sort) params.set('sort', options.sort);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<WebContact>>(`/web-contacts${query ? `?${query}` : ''}`);
}

export async function getContact(id: string): Promise<WebContact> {
  return fetchAPI<WebContact>(`/web-contacts/${id}`);
}

export async function getContactTimeline(id: string, limit?: number): Promise<Record<string, unknown>> {
  const query = limit ? `?limit=${limit}` : '';
  return fetchAPI<Record<string, unknown>>(`/web-contacts/${id}/timeline${query}`);
}

export async function recomputeContactScore(id: string): Promise<Record<string, unknown>> {
  return fetchAPI<Record<string, unknown>>(`/web-contacts/${id}/recompute-score`, { method: 'POST' });
}

export async function recomputeAllContacts(concurrency?: number): Promise<Record<string, unknown>> {
  return fetchAPI<Record<string, unknown>>('/web-contacts/recompute-all', {
    method: 'POST',
    body: JSON.stringify(concurrency ? { concurrency } : {}),
  });
}

// ============================================
// LEAD SCORING RULES  (/lead-scoring-rules)
// ============================================

export type ScoringRuleKind = 'event' | 'page_view' | 'utm' | 'frequency' | 'recency' | 'conversion';

export interface ScoringRule {
  id: string;
  name: string;
  description?: string | null;
  kind: ScoringRuleKind;
  points: number;
  max_per_contact?: number | null;
  is_active: boolean;
  match?: Record<string, unknown>;
  created_at: string;
  updated_at?: string;
  [key: string]: unknown;
}

export async function getScoringRules(options?: {
  isActive?: boolean;
  kind?: ScoringRuleKind;
}): Promise<PaginatedResponse<ScoringRule>> {
  const params = new URLSearchParams();
  if (typeof options?.isActive === 'boolean') params.set('is_active', String(options.isActive));
  if (options?.kind) params.set('kind', options.kind);
  const query = params.toString();
  return fetchAPI<PaginatedResponse<ScoringRule>>(`/lead-scoring-rules${query ? `?${query}` : ''}`);
}

export async function getScoringRule(id: string): Promise<ScoringRule> {
  return fetchAPI<ScoringRule>(`/lead-scoring-rules/${id}`);
}

export async function createScoringRule(body: {
  name: string;
  description?: string | null;
  kind: ScoringRuleKind;
  points: number;
  max_per_contact?: number | null;
  is_active?: boolean;
  match?: Record<string, unknown>;
}): Promise<ScoringRule> {
  return fetchAPI<ScoringRule>('/lead-scoring-rules', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateScoringRule(id: string, body: Partial<{
  name: string;
  description: string | null;
  kind: ScoringRuleKind;
  points: number;
  max_per_contact: number | null;
  is_active: boolean;
  match: Record<string, unknown>;
}>): Promise<ScoringRule> {
  return fetchAPI<ScoringRule>(`/lead-scoring-rules/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deleteScoringRule(id: string): Promise<{ success: boolean }> {
  return fetchAPI<{ success: boolean }>(`/lead-scoring-rules/${id}`, { method: 'DELETE' });
}

// ============================================
// WEB SEGMENTS  (/web-segments)
// ============================================

export interface WebSegment {
  id: string;
  name: string;
  match?: Record<string, unknown>;
  contact_count?: number;
  created_at: string;
  [key: string]: unknown;
}

export async function getSegments(): Promise<PaginatedResponse<WebSegment>> {
  return fetchAPI<PaginatedResponse<WebSegment>>('/web-segments');
}

export async function createSegment(body: { name: string; match?: Record<string, unknown> }): Promise<WebSegment> {
  return fetchAPI<WebSegment>('/web-segments', { method: 'POST', body: JSON.stringify(body) });
}

export async function deleteSegment(id: string): Promise<{ success: boolean }> {
  return fetchAPI<{ success: boolean }>(`/web-segments/${id}`, { method: 'DELETE' });
}

export async function getSegmentContacts(id: string): Promise<PaginatedResponse<WebContact>> {
  return fetchAPI<PaginatedResponse<WebContact>>(`/web-segments/${id}/contacts`);
}

export async function previewSegment(match: Record<string, unknown>): Promise<Record<string, unknown>> {
  return fetchAPI<Record<string, unknown>>('/web-segments/preview', {
    method: 'POST',
    body: JSON.stringify({ match }),
  });
}

// ============================================
// WEB ANALYTICS  (/web-analytics)
// Nota: el contrato real usa from/to (date-time), NO range.
// ============================================

export type AnalyticsSourceDimension = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'referrer';
export type AnalyticsTimeseriesMetric = 'sessions' | 'visitors' | 'conversions';

export interface AnalyticsOverview {
  sessions: number;
  visitors: number;
  conversions: number;
  conversion_rate: number;
  conversion_value: number;
}

export interface AnalyticsSourceRow {
  sessions: number;
  visitors: number;
  conversions: number;
  conversion_value: number;
  conversion_rate: number;
  [key: string]: unknown;
}

export interface AnalyticsSources {
  dimension: AnalyticsSourceDimension;
  data: AnalyticsSourceRow[];
}

export interface AnalyticsFunnelStep {
  key: string;
  label: string;
  count: number;
  pct_of_total: number;
  pct_of_prev: number;
}

export interface AnalyticsFunnel {
  data: AnalyticsFunnelStep[];
}

export interface AnalyticsTimeseriesPoint {
  day: string;
  value: number;
}

export interface AnalyticsTimeseries {
  metric: AnalyticsTimeseriesMetric;
  data: AnalyticsTimeseriesPoint[];
}

function analyticsRangeParams(range?: { from?: string; to?: string }): string {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getAnalyticsOverview(range?: { from?: string; to?: string }): Promise<AnalyticsOverview> {
  return fetchAPI<AnalyticsOverview>(`/web-analytics/overview${analyticsRangeParams(range)}`);
}

export async function getAnalyticsSources(options?: {
  from?: string;
  to?: string;
  dimension?: AnalyticsSourceDimension;
  limit?: number;
}): Promise<AnalyticsSources> {
  const params = new URLSearchParams();
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);
  if (options?.dimension) params.set('dimension', options.dimension);
  if (options?.limit) params.set('limit', String(options.limit));
  const query = params.toString();
  return fetchAPI<AnalyticsSources>(`/web-analytics/sources${query ? `?${query}` : ''}`);
}

export async function getAnalyticsFunnel(range?: { from?: string; to?: string }): Promise<AnalyticsFunnel> {
  return fetchAPI<AnalyticsFunnel>(`/web-analytics/funnel${analyticsRangeParams(range)}`);
}

export async function getAnalyticsTimeseries(options?: {
  from?: string;
  to?: string;
  metric?: AnalyticsTimeseriesMetric;
}): Promise<AnalyticsTimeseries> {
  const params = new URLSearchParams();
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);
  if (options?.metric) params.set('metric', options.metric);
  const query = params.toString();
  return fetchAPI<AnalyticsTimeseries>(`/web-analytics/timeseries${query ? `?${query}` : ''}`);
}

export async function getAnalyticsSessions(options?: {
  converted?: boolean;
  fingerprintHash?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<Record<string, unknown>>> {
  const params = new URLSearchParams();
  if (typeof options?.converted === 'boolean') params.set('converted', String(options.converted));
  if (options?.fingerprintHash) params.set('fingerprint_hash', options.fingerprintHash);
  if (options?.page) params.set('page', String(options.page));
  if (options?.pageSize) params.set('page_size', String(options.pageSize));
  const query = params.toString();
  return fetchAPI<PaginatedResponse<Record<string, unknown>>>(`/web-analytics/sessions${query ? `?${query}` : ''}`);
}
